import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../services/api/authApi";
import { showError, showSuccess } from "../../lib/toast";
import * as Yup from "yup";

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<ResetPasswordFormData>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters"),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password")], "Passwords do not match"),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (touched[name]) {
      validateField(name as keyof ResetPasswordFormData, {
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name as keyof ResetPasswordFormData, formData);
  };

  const validateField = (
    field: keyof ResetPasswordFormData,
    values: ResetPasswordFormData
  ) => {
    try {
      validationSchema.validateSyncAt(field as string, values);
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (e: any) {
      setErrors((prev) => ({ ...prev, [field]: e.message }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
    } catch (err: any) {
      const nextErrors: Partial<ResetPasswordFormData> = {};
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e: any) => {
          if (!nextErrors[e.path as keyof ResetPasswordFormData]) {
            nextErrors[e.path as keyof ResetPasswordFormData] = e.message;
          }
        });
      }
      setErrors(nextErrors);
      return;
    }

    const token = searchParams.get("token");
    if (!token) {
      showError("Invalid or missing reset token");
      return;
    }

    setIsSubmitting(true);

    try {
      const response: any = await authApi.resetPassword({
        token,
        password: formData.password,
      });

      const successMessage = response?.message || "Password has been reset successfully";
      showSuccess(successMessage);
      navigate("/login");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Breadcrumb */}
      <div className="py-3 font_two">
        <div className="container">
          <p className="text-muted small mb-0 font_two">
            <Link to="/" className="text-muted text-decoration-none">
              Home
            </Link>{" "}
            /<span className="fw-bold"> Reset Password</span>
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row py-4">
        {/* Left Content */}
        <div className="col-md-6 mb-4">
          <h4 className="display-6 mb-3">Reset Password</h4>
          <p className="text-muted font_two mb-3">New here? Create an account</p>

          <a
            href="/login"
            className="text-link"
          >
            Remember your password? Sign in
          </a>
        </div>

        {/* Right Form */}
        <div className="col-md-6">
          <form
            onSubmit={handleSubmit}
            className="bg-white p-4 rounded-3 shadow-sm"
          >
            <div className="mb-4">
              <label htmlFor="password" className="form-label fw-medium">
                New Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  // required
                  className="form-control form-control-sm custom-input"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {errors.password && (
                <small className="font_two text-danger d-block mt-1">{errors.password}</small>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label fw-medium">
                Confirm New Password <span className="text-danger">*</span>
              </label>
              <input
                type={"password"}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                // required
                className="form-control form-control-sm custom-input"
              />
              {errors.confirmPassword && (
                <small className="font_two text-danger d-block mt-1">{errors.confirmPassword}</small>
              )}
            </div>

            <div className="text-center pt-2">
              <button
                type="submit"
                className="btn btn-dark primary-btn w-50 rounded-pill"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating Password...
                  </>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
